import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Loading, Tab, TabList, Tabs, TextArea } from "@carbon/react";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import { NetworkError } from "../../components";
import { useUser, useNotification } from "../../context";
import { useLanguage } from "../../hooks";
import { queryKeys } from "../../hooks/queryKeys";
import {
  useDeletePurchaseRequestV2,
  usePurchaseRequestV2,
  useServicesToQuote,
  useUpdatePRStatus,
  useUpdatePurchaseRequestV2,
  useUpdateRequestItemV2,
} from "../../hooks/usePurchaseRequestsV2";
import {
  PurchaseRequestItemState,
  PurchaseRequestItemV2,
  PurchaseRequestStatus,
} from "../../types";
import { statusTag } from "./purchaseRequestRows";
import { getRequesterName } from "./purchaseRequestListHelpers";
import { useDetailItems } from "./Detail/useDetailItems";
import { useDetailPermissions } from "./Detail/useDetailPermissions";
import DetailItemsTab from "./Detail/DetailItemsTab";
import DetailGeneralTab from "./Detail/DetailGeneralTab";
import "./detail.scss";
import ServicesToQuoteCard from "../../components/ServicesToQuoteCard/ServicesToQuoteCard";

/**
 * The Purchase Request DETAIL viewer (`/purchase-requests/:id`), rebuilt on Carbon (Task 5) in
 * place of the pre-Carbon 1775-line Tailwind + heroicons screen. Split so every owned file stays
 * under the brief's ~400-line budget:
 * - `Detail/useDetailItems.ts` — URL-param filter/search state + item fetching + `activeItems`.
 * - `Detail/useDetailPermissions.ts` — the role-gating computation (owner / assigned-buyer / peers).
 * - `Detail/detailHelpers.ts` — pure budget-rollup / file-normalisation / change-detection helpers.
 * - `Detail/detailItemRows.tsx` — the item `DataTable` header/row mappers.
 * - `Detail/DetailItemFilters.tsx` — the item search + tag/category/buyer/state filters.
 * - `Detail/DetailItemsTab.tsx` — the item table (shared `DataTable` + `selection`), edit modal,
 *   material-info slide-over.
 * - `Detail/DetailItemInfoPanel.tsx` — the item material-info slide-over content.
 * - `Detail/DetailGeneralTab.tsx` — the general-info form + request summary + global budget.
 * - `Detail/EditItemModal.tsx` — the per-item edit modal.
 *
 * Behaviour preserved exactly: the item STATE MACHINE display (via the shared `statusTag`, no
 * completeness re-derived), bulk item-state reject, start-quoting (single + bulk → `/orders/from-request`),
 * the available-assigned-buyers facet (lean `UserSummaryDTO`), every filter, and all role gating.
 * The header renders as `PageHeader`'s `<h1>` (was an `<h2>`) — the one sanctioned selector-truth
 * change (see task-5-report.md). The route was renamed from `/purchase-requests-v2/:id` to
 * `/purchase-requests/:id` in Task 7 (`LegacyPurchaseRequestRedirect` covers the old path).
 */
const PurchaseRequestV2Detail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const langEs = useLanguage();
  const queryClient = useQueryClient();
  const { user, isRequester } = useUser();
  const { openNotification } = useNotification();

  const { data: request, isLoading, isError } = usePurchaseRequestV2(id);
  const { data: servicesToQuote } = useServicesToQuote(id);

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [globalCurrency, setGlobalCurrency] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [itemToReject, setItemToReject] =
    useState<PurchaseRequestItemV2 | null>(null);
  const [isSending, setIsSending] = useState(false);

  const items = useDetailItems(id, request);
  const permissions = useDetailPermissions(request, user, isRequester);

  const { mutate: deleteRequest, isPending: isDeletingRequest } =
    useDeletePurchaseRequestV2(() => {
      setDeleteModalOpen(false);
      navigate(-1);
    });

  const {
    mutateAsync: updatePRStatusAsync,
    isPending: isUpdatingPRStatus,
  } = useUpdatePRStatus(() => {});

  const { mutateAsync: updateItemAsync } = useUpdateRequestItemV2(() => {});
  const { mutateAsync: updateRequestAsync } = useUpdatePurchaseRequestV2(
    () => {}
  );

  const isBusy = isDeletingRequest || isUpdatingPRStatus || isSending;

  const handleSendForApproval = async () => {
    if (!request || !permissions.canEditRequest) return;
    const active = request.items.filter((item) => !item.deleted);
    if (!active.length) return;
    setIsSending(true);
    try {
      await Promise.all(
        active.map((item) =>
          updateItemAsync({
            itemId: item.id,
            body: { ...item, state: PurchaseRequestItemState.IN_APPROVAL },
          })
        )
      );
      await updateRequestAsync({
        id: request.id,
        body: { ...request, draft: false },
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.PURCHASE_REQUEST_V2, request.id],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS, request.id],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER],
        }),
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteRequest = () => {
    if (!request || !permissions.canEditRequest) return;
    deleteRequest(request.id);
  };

  // Refetch everything the detail + lists read, so a reject/resend is reflected
  // immediately (the hook's own invalidation keys off `res.request?.id`, which is
  // null on some responses — invalidating by the known route id here is what makes
  // the view actually refresh).
  const refreshDetail = (requestId: string) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2, requestId],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS, requestId],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2_BUYER_ITEMS],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER],
      }),
    ]);

  const handleRejectItem = (item: PurchaseRequestItemV2) => {
    setItemToReject(item);
    setRejectModalOpen(true);
  };

  const handleBuyerRejectConfirm = async () => {
    if (!permissions.canBuyerReject || !request) {
      setRejectModalOpen(false);
      return;
    }
    setIsSending(true);
    try {
      if (itemToReject) {
        await updateItemAsync({
          itemId: itemToReject.id,
          body: {
            ...itemToReject,
            state: PurchaseRequestItemState.REJECTED,
            rejectedMessage: rejectReason,
          },
        });
        setItemToReject(null);
      } else if (selectedItemIds.length > 0) {
        await Promise.all(
          selectedItemIds.map((itemId) => {
            const item = request.items.find((i) => i.id === itemId);
            return item
              ? updateItemAsync({
                  itemId: item.id,
                  body: {
                    ...item,
                    state: PurchaseRequestItemState.REJECTED,
                    rejectedMessage: rejectReason,
                  },
                })
              : Promise.resolve();
          })
        );
        setSelectedItemIds([]);
      } else {
        await updatePRStatusAsync({
          prId: request.id,
          request: {
            status: PurchaseRequestStatus.REJECTED,
            rejectedMessage: rejectReason,
          },
        });
      }
      await refreshDetail(request.id);
      openNotification(t("purchaseRequests.rejectedSuccess"));
    } finally {
      setIsSending(false);
      setRejectModalOpen(false);
      setRejectReason("");
    }
  };

  // Requester-side: flip one REJECTED item back to IN_APPROVAL and resend it to
  // the buyer (the PR rolls back up to IN_APPROVAL on recalc). Backend allows the
  // transition with no state guard.
  const handleResendItem = async (item: PurchaseRequestItemV2) => {
    if (!permissions.canEditRequest || !request) return;
    setIsSending(true);
    try {
      await updateItemAsync({
        itemId: item.id,
        body: { ...item, state: PurchaseRequestItemState.IN_APPROVAL },
      });
      await refreshDetail(request.id);
      openNotification(t("purchaseRequests.items.resendSuccess"));
    } finally {
      setIsSending(false);
    }
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setItemToReject(null);
    setRejectReason("");
  };

  const handleLaunchOrder = () => {
    const params = new URLSearchParams();
    params.set("isV2", "true");
    if (selectedItemIds.length) {
      params.set("itemIds", selectedItemIds.join(","));
    }
    navigate(`/orders/from-request?${params.toString()}`);
  };

  const canEditActions =
    permissions.canEditRequest &&
    !!request &&
    (request.draft ||
      request.overallStatus === PurchaseRequestStatus.REJECTED);

  const headerActions = useMemo(() => {
    if (!request) return null;
    return (
      <div className="pr-detail__actions">
        <Button kind="secondary" onClick={() => navigate(-1)}>
          {t("cta.back")}
        </Button>
        {canEditActions ? (
          <Button kind="danger" disabled={isBusy} onClick={() => setDeleteModalOpen(true)}>
            {t("cta.delete")}
          </Button>
        ) : null}
        {canEditActions ? (
          <Button onClick={handleSendForApproval} disabled={isBusy}>
            {isSending
              ? t("purchaseRequests.loading")
              : request.overallStatus === PurchaseRequestStatus.REJECTED
                ? t("cta.resend", { defaultValue: t("cta.send") })
                : t("cta.send")}
          </Button>
        ) : null}
        {permissions.isAssignedBuyer ? (
          <>
            {permissions.canBuyerReject ? (
              <Button
                kind="danger"
                disabled={isBusy}
                onClick={() => setRejectModalOpen(true)}
              >
                {t("purchaseRequests.reject")}
              </Button>
            ) : null}
            <Button
              disabled={isBusy || !selectedItemIds.length}
              onClick={handleLaunchOrder}
            >
              {t("cta.launch")}
              {selectedItemIds.length > 0 ? ` (${selectedItemIds.length})` : ""}
            </Button>
          </>
        ) : null}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, canEditActions, permissions, isBusy, isSending, selectedItemIds, t]);

  if (isLoading) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  if (isError || !request) {
    return <NetworkError message={t("purchaseRequests.error")} />;
  }

  const rejectQuestion = itemToReject
    ? t("purchaseRequests.rejectQuestion")
    : selectedItemIds.length > 0
      ? t("purchaseRequests.rejectSelectedItems", {
          defaultValue: "Are you sure you want to reject the selected items?",
        })
      : t("purchaseRequests.rejectQuestion");

  return (
    <div className="pr-detail">
      <PageHeader
        title={
          <span className="pr-detail__title">
            <span className="pr-detail__code">
              {request.purchaseRequestCode || " - "}
            </span>
            {request.name || t("purchaseRequests.detailTitle")}
          </span>
        }
        action={headerActions}
      />

      <div className="pr-detail__subheader">
        {statusTag(request.overallStatus, t)}
        <span className="pr-detail__requester">
          {t("purchaseRequests.fields.requester")}:{" "}
          {getRequesterName(request)}
        </span>
      </div>

      {request.overallStatus === PurchaseRequestStatus.REJECTED &&
      request.rejectedMessage ? (
        <div className="pr-detail__rejected-banner">
          <span className="pr-detail__rejected-label">
            {t("purchaseRequests.motive")}:
          </span>
          <span>{request.rejectedMessage}</span>
        </div>
      ) : null}

      <Tabs
        selectedIndex={selectedTab}
        onChange={({ selectedIndex }) => setSelectedTab(selectedIndex)}
      >
        <TabList aria-label={request.name || t("purchaseRequests.detailTitle")}>
          <Tab>{t("purchaseRequests.items.title")}</Tab>
          <Tab>{t("purchaseRequests.detailTitle")}</Tab>
        </TabList>
      </Tabs>

      <div className="pr-detail__tab-content">
        {selectedTab === 0 ? (
          <ServicesToQuoteCard services={servicesToQuote ?? []} />
        ) : null}
        {selectedTab === 0 ? (
          <DetailItemsTab
            id={id}
            items={items}
            permissions={permissions}
            globalCurrency={globalCurrency}
            selectedItemIds={selectedItemIds}
            onSelectedItemIdsChange={setSelectedItemIds}
            onRejectItem={handleRejectItem}
            onResendItem={handleResendItem}
            isBusy={isBusy}
            langEs={langEs}
          />
        ) : null}
        {selectedTab === 1 ? (
          <DetailGeneralTab
            request={request}
            activeItems={items.activeItems}
            canEditRequest={permissions.canEditRequest}
            isBusy={isBusy}
            onGlobalCurrencyChange={setGlobalCurrency}
          />
        ) : null}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title={t("purchaseRequests.modalTitleDraft")}
        body={t("purchaseRequests.modalDraft")}
        confirmLabel={t("cta.confirm")}
        onConfirm={handleDeleteRequest}
        onCancel={() => setDeleteModalOpen(false)}
        isPending={isDeletingRequest}
      />

      <ConfirmDialog
        open={rejectModalOpen}
        danger
        title={
          itemToReject
            ? `${t("purchaseRequests.reject")}: ${
                itemToReject.material?.globalMaterialName ?? ""
              }`
            : t("purchaseRequests.reject")
        }
        body={
          <div className="pr-detail__reject-body">
            <p>{rejectQuestion}</p>
            <TextArea
              id="pr-detail-reject-reason"
              labelText={t("purchaseRequests.motive")}
              rows={3}
              value={rejectReason}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                setRejectReason(event.target.value)
              }
              placeholder={t("purchaseRequests.motive")}
            />
          </div>
        }
        confirmLabel={t("cta.reject")}
        onConfirm={handleBuyerRejectConfirm}
        onCancel={closeRejectModal}
        isPending={isSending || isUpdatingPRStatus}
        // Faithful restoration of the pre-Carbon guard: a whole-request reject (no single item,
        // no selected items) must not be confirmable with a blank reason. Per-item / bulk-item
        // rejects stay confirmable without a reason, exactly as before.
        confirmDisabled={
          !itemToReject && selectedItemIds.length === 0 && !rejectReason
        }
      />
    </div>
  );
};

export default PurchaseRequestV2Detail;
